import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, Paperclip, Send, Loader2, Trash2, Clock, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { useFieldDraft } from '../hooks/useFieldDraft';
import ScheduleSendModal from './ScheduleSendModal';
import RecipientPicker from './RecipientPicker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Max attachment payload Gmail accepts via send is 25MB (35MB encoded).
const MAX_TOTAL_ATTACHMENT_BYTES = 25 * 1024 * 1024;

function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // result is "data:<mime>;base64,<b64>"
      const result = reader.result || '';
      const idx = String(result).indexOf('base64,');
      resolve(idx >= 0 ? String(result).slice(idx + 7) : String(result));
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

function quoteOriginal(message) {
  if (!message) return '';
  const dateStr = message.date ? new Date(message.date).toLocaleString() : '';
  const from = message.from || message.from_email || '';
  const quoted = (message.text || (message.html ? '(HTML content)' : '')).split('\n').map(l => `> ${l}`).join('\n');
  return `\n\nOn ${dateStr}, ${from} wrote:\n${quoted}\n`;
}

/**
 * ComposeModal — used for new emails, replies, and forwards.
 *
 * Props:
 *   open: boolean
 *   onClose: fn()
 *   onSent: fn(result) — called after successful send
 *   mode: 'new' | 'reply' | 'replyAll' | 'forward'
 *   replyTo: thread message object (the message being replied to / forwarded)
 *   senderEmail: connected mailbox email (for excluding self from reply-all)
 */
export default function ComposeModal({ open, onClose, onSent, mode = 'new', replyTo = null, senderEmail = '', mailbox: mailboxProp = null, initialTo = '', initialCc = '', initialSubject = '', initialBody = '', attachDocumentIds = null, attachLabel = '' }) {
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [showCc, setShowCc] = useState(false);
  const [showBcc, setShowBcc] = useState(false);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef(null);

  // From mailbox selector (shared admin inbox vs the user's personal Gmail)
  const [sharedConn, setSharedConn] = useState(null);
  const [personalConn, setPersonalConn] = useState(null);
  const [mailbox, setMailbox] = useState(() => {
    if (mailboxProp) return mailboxProp;
    try {
      return window.localStorage.getItem('compose:lastMailbox') || 'shared';
    } catch {
      return 'shared';
    }
  });
  const [showScheduler, setShowScheduler] = useState(false);

  // Load both mailbox connection states once when modal opens so the
  // From dropdown can disable unavailable options.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    Promise.all([
      axios.get(`${API}/communications/connection-status`).catch(() => null),
      axios.get(`${API}/communications/personal/connection-status`).catch(() => null),
    ]).then(([s, p]) => {
      if (cancelled) return;
      setSharedConn(s?.data || null);
      setPersonalConn(p?.data || null);
      // If a reply/forward locks the mailbox via prop, respect that; otherwise
      // fall back to whichever mailbox is actually connected.
      if (!mailboxProp) {
        const sharedOk = !!s?.data?.connected;
        const personalOk = !!p?.data?.connected;
        const desired = window.localStorage.getItem('compose:lastMailbox') || 'shared';
        if (desired === 'personal' && !personalOk && sharedOk) setMailbox('shared');
        else if (desired === 'shared' && !sharedOk && personalOk) setMailbox('personal');
        else setMailbox(desired);
      }
    });
    return () => { cancelled = true; };
  }, [open, mailboxProp]);

  // Persist last-used mailbox choice for next time
  useEffect(() => {
    if (!open || mailboxProp) return;
    try { window.localStorage.setItem('compose:lastMailbox', mailbox); } catch {}
  }, [mailbox, open, mailboxProp]);

  // Preserve in-progress new compose so session timeout doesn't lose it.
  // We only enable drafting for the "new" mode — replies/forwards prefill
  // with quoted content and restoring on top of that gets messy.
  const draftKey = (mode === 'new' && open) ? 'compose:new' : null;
  const { clearDraft: clearComposeDraft } = useFieldDraft(draftKey, body, setBody);
  const subjectDraftKey = (mode === 'new' && open) ? 'compose:new:subject' : null;
  const { clearDraft: clearSubjectDraft } = useFieldDraft(subjectDraftKey, subject, setSubject);

  // Prefill based on mode whenever the modal opens with new context
  useEffect(() => {
    if (!open) return;
    if (mode === 'reply' && replyTo) {
      setTo(replyTo.from_email || replyTo.from || '');
      setCc('');
      setSubject(replyTo.subject?.startsWith('Re:') ? replyTo.subject : `Re: ${replyTo.subject || ''}`);
      setBody(quoteOriginal(replyTo));
      setShowCc(false); setShowBcc(false);
    } else if (mode === 'replyAll' && replyTo) {
      setTo(replyTo.from_email || replyTo.from || '');
      // CC = original To + Cc minus the connected mailbox itself
      const others = [replyTo.to, replyTo.cc].filter(Boolean).join(', ');
      const cleaned = others
        .split(',')
        .map(s => s.trim())
        .filter(s => s && !s.toLowerCase().includes((senderEmail || '').toLowerCase()))
        .join(', ');
      setCc(cleaned);
      setShowCc(!!cleaned);
      setSubject(replyTo.subject?.startsWith('Re:') ? replyTo.subject : `Re: ${replyTo.subject || ''}`);
      setBody(quoteOriginal(replyTo));
      setShowBcc(false);
    } else if (mode === 'forward' && replyTo) {
      setTo('');
      setCc('');
      setSubject(replyTo.subject?.startsWith('Fwd:') ? replyTo.subject : `Fwd: ${replyTo.subject || ''}`);
      const forwardHeader = `\n\n---------- Forwarded message ----------\nFrom: ${replyTo.from || ''}\nDate: ${replyTo.date ? new Date(replyTo.date).toLocaleString() : ''}\nSubject: ${replyTo.subject || ''}\nTo: ${replyTo.to || ''}\n\n${replyTo.text || ''}\n`;
      setBody(forwardHeader);
      setShowCc(false); setShowBcc(false);
    } else {
      setTo(initialTo || ''); setCc(initialCc || ''); setBcc('');
      setSubject(initialSubject || ''); setBody(initialBody || '');
      setShowCc(!!initialCc); setShowBcc(false);
    }
    setAttachments([]);
  }, [open, mode, replyTo, senderEmail, initialTo, initialCc, initialSubject, initialBody]);

  if (!open) return null;

  const totalAttachmentBytes = attachments.reduce((s, a) => s + a.size, 0);

  const handleFiles = async (files) => {
    const list = Array.from(files || []);
    const next = [...attachments];
    for (const f of list) {
      if (totalAttachmentBytes + f.size > MAX_TOTAL_ATTACHMENT_BYTES) {
        toast.error(`"${f.name}" would push attachments over 25MB`);
        continue;
      }
      try {
        const b64 = await readFileAsBase64(f);
        next.push({
          filename: f.name,
          mime_type: f.type || 'application/octet-stream',
          size: f.size,
          content_base64: b64,
        });
      } catch (e) {
        toast.error(`Failed to read ${f.name}`);
      }
    }
    setAttachments(next);
  };

  const removeAttachment = (idx) => {
    setAttachments(prev => prev.filter((_, i) => i !== idx));
  };

  const parseRecipients = (s) => s.split(/[,;]/).map(p => p.trim()).filter(Boolean);

  // Build the SendMessageRequest payload from current form state.
  // Returns null if validation failed (and shows a toast).
  const buildPayload = () => {
    const recipients = parseRecipients(to);
    if (recipients.length === 0) {
      toast.error('Add at least one recipient');
      return null;
    }
    if (!subject.trim()) {
      if (!window.confirm('Send without a subject?')) return null;
    }
    if (!body.trim()) {
      if (!window.confirm('Send without a body?')) return null;
    }
    const payload = {
      to: recipients,
      cc: showCc && cc ? parseRecipients(cc) : null,
      bcc: showBcc && bcc ? parseRecipients(bcc) : null,
      subject: subject,
      mailbox: mailbox,
      body_text: body,
      body_html: `<div style="white-space:pre-wrap;font-family:Arial,Helvetica,sans-serif;">${body
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')}</div>`,
      attachments: attachments.map(a => ({
        filename: a.filename,
        mime_type: a.mime_type,
        content_base64: a.content_base64,
      })),
    };
    if ((mode === 'reply' || mode === 'replyAll' || mode === 'forward') && replyTo) {
      payload.thread_id = replyTo.thread_id;
      if (replyTo.rfc_message_id) payload.in_reply_to_message_id = replyTo.rfc_message_id;
      if (replyTo.references) payload.references = replyTo.references;
    }
    return payload;
  };

  const handleSend = async () => {
    const payload = buildPayload();
    if (!payload) return;
    setSending(true);
    try {
      let res;
      if (attachDocumentIds && attachDocumentIds.length > 0) {
        // Route through the credentials/email-attach endpoint so the files
        // are pulled from GridFS on the server side (avoids uploading them
        // from the browser, and gracefully reports any missing files).
        res = await axios.post(`${API}/admin/credentials/email-attach`, {
          document_ids: attachDocumentIds,
          to: payload.to,
          cc: payload.cc,
          bcc: payload.bcc,
          subject: payload.subject,
          body_text: payload.body_text,
          body_html: payload.body_html,
          mailbox: payload.mailbox,
        });
        const skipped = res.data?.skipped || [];
        if (skipped.length > 0) {
          toast.warning(`Sent (${res.data.attached_count}). Skipped ${skipped.length} unreadable file(s).`);
        } else {
          toast.success(`Email sent with ${res.data?.attached_count || 0} attachment(s)`);
        }
      } else {
        res = await axios.post(`${API}/communications/messages/send`, payload);
        toast.success('Email sent');
      }
      clearComposeDraft();
      clearSubjectDraft();
      onSent?.(res.data);
      onClose?.();
    } catch (e) {
      console.error('Send failed:', e);
      toast.error(`Send failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSending(false);
    }
  };

  const handleSchedule = async (isoSendAt, humanLabel) => {
    const payload = buildPayload();
    if (!payload) return;
    setSending(true);
    try {
      if (attachDocumentIds && attachDocumentIds.length > 0) {
        await axios.post(`${API}/admin/credentials/email-attach`, {
          document_ids: attachDocumentIds,
          to: payload.to,
          cc: payload.cc,
          bcc: payload.bcc,
          subject: payload.subject,
          body_text: payload.body_text,
          body_html: payload.body_html,
          mailbox: payload.mailbox,
          send_at: isoSendAt,
        });
      } else {
        await axios.post(`${API}/communications/messages/schedule`, {
          message: payload,
          send_at: isoSendAt,
        });
      }
      toast.success(`Scheduled for ${humanLabel}`);
      clearComposeDraft();
      clearSubjectDraft();
      setShowScheduler(false);
      onSent?.({ scheduled: true, send_at: isoSendAt });
      onClose?.();
    } catch (e) {
      console.error('Schedule failed:', e);
      toast.error(`Schedule failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black/50 flex items-end md:items-center justify-center p-0 md:p-6" data-testid="compose-modal">
      <div className="bg-white w-full md:max-w-3xl md:rounded-xl shadow-2xl flex flex-col max-h-[95vh] overflow-hidden">
        <header className="flex items-center justify-between px-4 py-3 border-b bg-muted/30">
          <h2 className="text-base font-semibold">
            {mode === 'reply' && 'Reply'}
            {mode === 'replyAll' && 'Reply All'}
            {mode === 'forward' && 'Forward'}
            {mode === 'new' && 'New Message'}
          </h2>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0" data-testid="compose-close-btn">
            <X size={18} />
          </Button>
        </header>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {/* From mailbox selector — only shown when not locked via prop and at least one mailbox is connected */}
          {!mailboxProp && (sharedConn?.connected || personalConn?.connected) && (
            <div className="flex items-center gap-2">
              <label className="text-xs text-muted-foreground w-12">From</label>
              <select
                value={mailbox}
                onChange={e => setMailbox(e.target.value)}
                disabled={sending}
                data-testid="compose-from-mailbox"
                className="flex-1 h-9 px-2 text-sm border rounded-md bg-background focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="shared" disabled={!sharedConn?.connected}>
                  Shared Admin Inbox{sharedConn?.email ? ` — ${sharedConn.email}` : ''}{!sharedConn?.connected ? ' (not connected)' : ''}
                </option>
                <option value="personal" disabled={!personalConn?.connected}>
                  My Work Email{personalConn?.email ? ` — ${personalConn.email}` : ''}{!personalConn?.connected ? ' (connect under Settings)' : ''}
                </option>
              </select>
            </div>
          )}
          <div className="flex items-center gap-2">
            <label className="text-xs text-muted-foreground w-12">To</label>
            <RecipientPicker
              value={to}
              onChange={setTo}
              placeholder="Start typing a name or email…"
              disabled={sending}
              data-testid="compose-to"
            />
            <div className="flex items-center gap-1 text-xs">
              {!showCc && (
                <button type="button" onClick={() => setShowCc(true)} className="text-muted-foreground hover:text-foreground">Cc</button>
              )}
              {!showBcc && (
                <button type="button" onClick={() => setShowBcc(true)} className="text-muted-foreground hover:text-foreground">Bcc</button>
              )}
            </div>
          </div>
          {showCc && (
            <div className="flex items-center gap-2">
              <label className="text-xs text-muted-foreground w-12">Cc</label>
              <Input value={cc} onChange={e => setCc(e.target.value)} className="flex-1" data-testid="compose-cc" />
              <button type="button" onClick={() => { setShowCc(false); setCc(''); }} className="text-muted-foreground hover:text-foreground">
                <X size={14} />
              </button>
            </div>
          )}
          {showBcc && (
            <div className="flex items-center gap-2">
              <label className="text-xs text-muted-foreground w-12">Bcc</label>
              <Input value={bcc} onChange={e => setBcc(e.target.value)} className="flex-1" data-testid="compose-bcc" />
              <button type="button" onClick={() => { setShowBcc(false); setBcc(''); }} className="text-muted-foreground hover:text-foreground">
                <X size={14} />
              </button>
            </div>
          )}
          <div className="flex items-center gap-2">
            <label className="text-xs text-muted-foreground w-12">Subject</label>
            <Input
              value={subject}
              onChange={e => setSubject(e.target.value)}
              placeholder="Subject"
              data-testid="compose-subject"
              className="flex-1"
            />
          </div>
          {attachDocumentIds && attachDocumentIds.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-md px-3 py-2 text-sm text-blue-900 flex items-center gap-2" data-testid="compose-credential-attach-banner">
              <Paperclip size={14} />
              <span className="font-medium">{attachDocumentIds.length}</span>
              <span>credential document{attachDocumentIds.length !== 1 ? 's' : ''} will be attached{attachLabel ? ` (${attachLabel})` : ''} when you send.</span>
            </div>
          )}
          <Textarea
            value={body}
            onChange={e => setBody(e.target.value)}
            placeholder="Write your message…"
            className="min-h-[280px] resize-y font-sans text-sm"
            data-testid="compose-body"
          />

          {attachments.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Attachments ({attachments.length}) — {Math.round(totalAttachmentBytes / 1024)} KB
              </p>
              <div className="flex flex-wrap gap-2">
                {attachments.map((a, i) => (
                  <div key={i} className="flex items-center gap-2 px-2.5 py-1.5 rounded-md border bg-muted/30 text-sm">
                    <Paperclip size={14} />
                    <span className="max-w-[200px] truncate">{a.filename}</span>
                    <span className="text-xs text-muted-foreground">{Math.round(a.size / 1024)} KB</span>
                    <button
                      type="button"
                      onClick={() => removeAttachment(i)}
                      className="text-muted-foreground hover:text-red-600"
                      data-testid={`remove-attachment-${i}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <footer className="flex items-center justify-between px-4 py-3 border-t bg-muted/30">
          <div>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              onChange={e => handleFiles(e.target.files)}
              className="hidden"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={sending}
              data-testid="compose-attach-btn"
              className="gap-2"
            >
              <Paperclip size={14} />
              Attach
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={onClose} disabled={sending}>
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowScheduler(true)}
              disabled={sending}
              data-testid="compose-schedule-btn"
              className="gap-2"
              title="Schedule send for later"
            >
              <Clock size={14} />
              Send later
            </Button>
            <Button
              onClick={handleSend}
              disabled={sending}
              data-testid="compose-send-btn"
              className="gap-2"
            >
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              {sending ? 'Sending…' : 'Send'}
            </Button>
          </div>
        </footer>
      </div>

      <ScheduleSendModal
        open={showScheduler}
        onClose={() => setShowScheduler(false)}
        onConfirm={handleSchedule}
      />
    </div>
  );
}
